import React, { useState, useEffect } from 'react';
import {Grid, Typography, Card, IconButton, Icon, TextField} from '@mui/material'
import InputDateForm from '../../../components/forms/InputDateForm';
import InputSelectForm from '../../../components/forms/InputSelectForm';
import { grey } from "@mui/material/colors";
import background from '../../../assets/registration/blue-wave.jpg'
import AddressAdjustmentModal from '../modals/AddressAdjustmentModal';
import {makeStyles} from '@mui/styles'
import ContainedButton from '../../../components/actions/ContainedButton';
import DisplayError from '../../../components/alerts/DisplayError';
import InputForm from '../../../components/forms/InputForm';
import Iconify from '../../../components/iconify';

/**
 * @module Layouts/Registration/views/PersonalDataForm
 * @description The code defines a functional component called `PersonalDataForm` that renders a form for
users to register for an account. The component uses various input components such as `InputForm`,
`InputSelectForm`, and `InputDateForm` to collect user information such as name, email, birth date,
and address. The component also includes an eye button that toggles the visibility of the user's
social security number. The component uses the `useEffect` hook to fetch user data when the
component mounts and displays an error message if there is an error. 
 * @returns {React.ReactElement} The `PersonalDataForm` component is being returned as a View.
 */

/**
 * 
 * @typedef PersonalDataForm
 * @property {MUIStyles} classes - The styles for the component 
 * @property {Boolean} loading - Indicates if initialization process is taking place
 * @property {Boolean} sending - Indicates if a process has started
 * @property {Object | String | Boolean} error - Variable that handles error, it can be a boolean, a string or an object 

 * @property {Object} form - Form with fields of the personal data of the end user
 * @property {Function} onCheckAddress - This function checks if the address information is valid according to Smarty Service
 * and allows to continue or not the Enrollment Process

 */


const PersonalDataForm = (props) => {

    const classes = useStyles()
    const [visible, setVisible] = useState(false)

    const {
        system, data, actions, form, modals
    } = props

    useEffect(() => {
        const onInitModule = async() => {
            //console.log('Pedir informacio de usuario')
            try {
                await actions.onRequestUserData()
            } catch (error) {
                console.log(error)
                actions.onResetForm()
            }
            
        }
        onInitModule()
    }, [])

    const eyeButton = (
        <IconButton size='small'
            onClick={() => setVisible(!visible)}
        ><Iconify icon={!visible ? 'el:eye-open' : 'el:eye-close'} /></IconButton>
    )

    let _ssnForm = {...form.ssn}
    _ssnForm.config.type = visible ? 'tel' : 'password'

    return ( 
        <div>
            <AddressAdjustmentModal sending={system.sending}
                addresses={data.smartyAddresses} 
                open={modals.address_adjustment} 
                onClose={() => actions.onUpdateModalStatus('address_adjustment', false)}
                onEnrollCardholder={actions.onEnrollCardholder}
            />
            <div >
                <div style={{marginBottom:32}}>
                    <Typography>
                    Please complete the form to register for an account.
                    </Typography>
                </div>
                <div><Typography variant='subtitle1' className={classes.subtitle}>Cardholder's Information</Typography></div>
                <div>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <InputForm data={form.first_name} onChange={actions.onChangeForm} forceUpperCase />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputForm data={form.surname} onChange={actions.onChangeForm} forceUpperCase/>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputForm data={form.email} onChange={actions.onChangeForm} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputSelectForm data={form.sex_id} onChange={actions.onChangeForm} />
                        </Grid>
                        {/*<Grid item xs={12} md={6}>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <div style={{width:100}}>
                                        <InputSelectForm data={form.alt_country_code_area} onChange={actions.onChangeForm} />
                                    </div>
                                </Grid>
                                <Grid item xs>
                                    <InputForm data={form.phone_number} onChange={actions.onChangeForm} />
                                </Grid>
                            </Grid>
                        </Grid>*/}
                        <Grid item xs={12} md={6}>
                            <InputDateForm data={form.birth_date} onChange={actions.onChangeForm}/>
                            
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputForm data={_ssnForm} onChange={actions.onChangeForm}  inputProps={{
                                pattern:"[0-9]*",
                                inputmode:"numeric"
                            }}
                            endAdornment={eyeButton}
                            />
                        </Grid>


                    </Grid>
                </div>
                <div className={classes.divider}></div>
                <div><Typography variant='subtitle1' className={classes.subtitle}>Address Information</Typography></div>
                <div>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <InputForm data={form.street_line_one} onChange={actions.onChangeForm} forceUpperCase/>
                        </Grid>
                        {/*<Grid item xs={12}>
                            <InputForm data={form.street_line_two} onChange={actions.onChangeForm} forceUpperCase/>
                    </Grid>*/}
                        <Grid item xs={12} md={6}>
                            <InputForm data={form.zip_code} onChange={actions.onChangeForm} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputForm disabled data={form.city} onChange={actions.onChangeForm} forceUpperCase/>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputForm disabled data={form.state} onChange={actions.onChangeForm} forceUpperCase/>
                        </Grid>


                    </Grid>
                </div>
                
                <div style={{marginTop:32}}>
                    <DisplayError>{system.error}</DisplayError>
                    <ContainedButton loading={system.sending} fullWidth color='primaryDark'
                        onClick={actions.onCheckAddress}
                    >CONTINUE</ContainedButton>
                </div>
            </div>
        </div>
     );
}

const useStyles = makeStyles(theme => ({
    root:{
        backgroundImage:`url(${background})`,
        width:'100%',
        height:'100vh',
        position:'relative'
    },
    subroot:{
        overflowY:'auto',
        overflowX:'hidden',
        maxHeight:'100vh',

    },
    container:{
        width:800,
        margin:'auto',
        paddingBottom:80,
        boxSizing:'border-box',
        [theme.breakpoints.down('sm')]:{
            width:'100%',
            padding:16,
            paddingBottom:40,
        }
    },
    title:{
        color:'white',
        marginTop:80,
        fontWeight:600,
        marginBottom:16,
        [theme.breakpoints.down('sm')]:{
            fontSize:36,
            marginTop:40,
        }

    },
    content:{
        color:'white',
        margin:'0px auto',
        marginTop:16,
        width:'100%',
        maxWidth:650,
        textAlign:'center',
        [theme.breakpoints.down('sm')]:{
            fontSize:16,
            maxWidth:'100%',
        }
    },
    card:{
        boxShadow:'7px 7px 20px rgb(0 0 0 / 10%)',
        borderRadius:20,
        marginTop:32,
        padding:'30px 32px'
    },
    subtitle:{
        fontWeight:600,
        marginBottom:16
    },
    divider:{
        background:grey[300],
        height:1,
        margin:'32px 0px'
    },
    recaptcha:{
        margin:'32px 0px'
    }
}))
 
export default PersonalDataForm;